import type { PagePath } from '@gov-nx/web/Router/helpertypes';
import { pageRouteResolver } from '@gov-nx/web/Router/routeResolver';

/**
 * `failure` (Result=0 for a reason other than user cancel) is intentionally
 * absent: that branch never returns the user to their origin, it always resolves
 * the temporary submission and lands on its detail.
 *
 * `cancelled` has no rule: a cancel keeps the submission, so the origin lives.
 */
export type AuthorizationOutcome = 'success' | 'cancelled';

interface LandingRule {
	outcomes: AuthorizationOutcome[];
	matches: (returnUrl: string) => boolean;
	redirectTo: () => string;
}

/** Matches routes nested under `path`, not the section index itself. */
const isUnder =
	(path: PagePath) =>
	(returnUrl: string): boolean =>
		returnUrl.startsWith(`${pageRouteResolver(path)}/`);

// Origins that cannot survive a given outcome, overriding the "return the user
// where they started" default. First match wins.
const LANDING_RULES: LandingRule[] = [
	{
		// Success consumes the temporary form602 submission on the BE, so the
		// "Čeká na vyřízení" detail the flow started from would 404 ("Autorizace
		// neexistuje"). The item has moved to "Podání" by then.
		outcomes: ['success'],
		matches: isUnder('moje-podani.autorizace'),
		redirectTo: () => pageRouteResolver('moje-podani'),
	},
];

export const resolveLandingUrl = (
	outcome: AuthorizationOutcome,
	returnUrl: string
): string => {
	const rule = LANDING_RULES.find(
		(candidate) =>
			candidate.outcomes.includes(outcome) && candidate.matches(returnUrl)
	);

	return rule ? rule.redirectTo() : returnUrl;
};
